import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ApplyColumnStateParams } from 'ag-grid-community';
import { PermissionService } from '../permission.service';
import { UserInstanceService } from '../../User/userInstance.service';
import { BusyIndicatorService } from '../../Busy-Indicator';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { AgGridColumns, AgGridFilterParams, AgGridOptionsBuilder } from '../../Compliance/AgGrid';
import { ICellRendererParamsForInstanceRightGridAction, InstanceRightGridActionCellRendererComponent } from './agGridActionCellRenderer.component';
import { NavigationService } from '../../Layout/navigation.service';
import { UserGroupsService } from '../../User-Group/userGroup.service';
import { RestrictService } from '../../Common/Permissions/restrict.service';
import * as _ from 'lodash';
import { UserGroupPanel } from 'src/app/User-Group/User-Group-Detail/userGroupDetail.component';

export interface PermissionWithOptions {
    right: Core.InstanceRightModel;
    rightOptions: Core.InstanceRightModel[];
    isDeleted: boolean;
}

@Component({
    templateUrl: './instancePermissionPanel.component.html',
    selector: 'instance-permission-panel',
    standalone: false
})
export class InstancePermissionPanelComponent implements OnInit {
    constructor(
        private readonly _permissionService: PermissionService,
        private readonly _busyIndicatorService: BusyIndicatorService,
        private readonly _userGroupsService: UserGroupsService,
        public readonly userInstanceService: UserInstanceService,
        private readonly _navigationService: NavigationService,
        private readonly _restrictService: RestrictService
    ) {
    }

    @Input() userGroup: Core.GroupModel;
    @Input() canEdit: boolean;
    @Input() userSetup: boolean;
    @Output() expandClicked: EventEmitter<UserGroupPanel> = new EventEmitter<UserGroupPanel>();

    gridOptions: GridOptions = new AgGridOptionsBuilder({
        suppressHorizontalScroll: true
    })
        .withContext(this)
        .withLoadingOverlay()
        .withMultipleColumnSort()
        .withTextSelection()
        .build();
    gridApi: GridApi;

    canEnterEditMode: boolean = true;
    editMode: boolean = false;
    instanceId: number;
    isExpanded: boolean = false;
    gridIsDirty: boolean = false;

    allPermissions: PermissionWithOptions[];
    assignedPermissions: PermissionWithOptions[];
    availablePermissions: PermissionWithOptions[];

    private _originalAllPermissions: PermissionWithOptions[];


    private _gridDataSource: any;

    async ngOnInit(): Promise<void> {
        this.instanceId = this.userInstanceService.getSelectedInstance().instanceId;
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;

        const columns: ColDef[] = [
            {
                headerName: 'Permission',
                field: 'right.screenType',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            },
            {
                headerName: 'Access Level',
                field: 'right.rights',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth,
                cellRenderer: InstanceRightGridActionCellRendererComponent,
                cellRendererParams: {
                    canEdit: () => {
                        return this.editMode && this.canEdit;
                    },
                    permissionChanged: () => {
                        this.gridIsDirty = true;
                    }
                } as ICellRendererParamsForInstanceRightGridAction
            }
        ];

        const defaultSortModel: ApplyColumnStateParams = {
            state: [{ colId: 'right.screenType', sort: 'asc' }],
            defaultState: { sort: null }
        };

        this.gridApi.setGridOption('columnDefs', columns);
        this.gridApi.applyColumnState(defaultSortModel);
        this.gridApi.sizeColumnsToFit();
        this.setDataSource();
    }

    async setDataSource(): Promise<void> {
        if (!this.gridApi || this._gridDataSource) {
            return;
        }

        try {
            this.gridApi && this.gridApi.setGridOption("loading", true);
            await this.loadPermissions();
            this.gridApi.setGridOption('rowData', this.allPermissions);
            this.gridApi && this.gridApi.setGridOption("loading", false);
        } finally {
            this.gridApi && this.gridApi.setGridOption("loading", false);
        }

    }

    private async loadPermissions(): Promise<void> {
        const assignedPermissions = await this._permissionService.getGroupInstanceRights(this.userGroup.groupID);
        await this._initPermissions(assignedPermissions);
    }

    private async _initPermissions(assignedPermissions: Core.InstanceRightModel[]): Promise<void> {
        const availablePermissions = await this._permissionService.getAvailableGroupInstanceRights(this.userGroup.groupID);

        this.assignedPermissions = _.map(assignedPermissions, permission => {
            const otherOptions = _.filter(availablePermissions, x => x.screenTypeID === permission.screenTypeID);
            const noneOption: Core.InstanceRightModel = {
                instanceRightID: -1,
                rightsID: -1,
                name: permission.name,
                rights: 'None',
                screenType: permission.screenType,
                screenTypeID: -1
            };

            return {
                right: permission,
                rightOptions: _.sortBy([...otherOptions, permission, noneOption], 'rightsID'),
                isDeleted: false
            };
        });

        this.availablePermissions = _.chain(availablePermissions)
            .reject(x => _.some(assignedPermissions, { screenTypeID: x.screenTypeID }))
            .groupBy('screenTypeID')
            .toArray()
            .map(rightOptions => {
                const noneOption: Core.InstanceRightModel = {
                    instanceRightID: -1,
                    rightsID: -1,
                    name: rightOptions[0].name,
                    rights: 'None',
                    screenType: rightOptions[0].screenType,
                    screenTypeID: -1
                };
                return {
                    right: noneOption,
                    rightOptions: _.sortBy([...rightOptions, noneOption], 'rightsID'),
                    isDeleted: false
                };
            })
            .value();

        this.allPermissions = _.sortBy(this.assignedPermissions.concat(this.availablePermissions), 'right.screenType');

        this.gridApi.setGridOption('rowData', this.allPermissions);
    }

    edit(): void {
        this._originalAllPermissions = _.cloneDeep(this.allPermissions);
        this.editMode = true;
        this._userGroupsService.setPanelEdit(this.editMode);
        this._navigationService.enableNavWarning('Editing is in progress. Are you sure you want to leave?');
    }

    cancel(): void {
        this.allPermissions = this._originalAllPermissions;
        this.setDataSource();
        this._exitEditMode();
    }

    async save(): Promise<void> {
        const instanceRightsIds = this.allPermissions
            .filter(a => a.right.rightsID > -1)
            .map(x => x.right.instanceRightID);

        const busyRef = this._busyIndicatorService.show({ message: 'Updating Instance Rights' });
        try {
            const assignedPermissions = await this._permissionService.updateGroupInstanceRights(this.userGroup.groupID, instanceRightsIds);
            await this._initPermissions(assignedPermissions);

            this._exitEditMode();
        } catch (e2) {
            if (e2 && e2.status !== 422) {
                return Promise.reject(e2);
            }
        } finally {
            await busyRef.hide();
        }
    }

    expandCollapse(expand: boolean): void {
        this.expandClicked.emit('instancePermission');
        setTimeout(() => {
            this.gridApi.sizeColumnsToFit();
        });
        this.isExpanded = expand;
    }

    private _exitEditMode(): void {
        this.editMode = false;
        this._userGroupsService.setPanelEdit(this.editMode);
        this._navigationService.disableNavWarning();
        this.gridIsDirty = false;
    }
}
